import type { ApiErrorBody } from "./types";
import { getAuthCredentials } from "@/store/useAuthStore";
import { isViewAsControlPath, viewAsHeaderValue } from "@/lib/viewAs";

const API_BASE = "/api/v1";

export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, unknown>,
    public readonly traceId?: string,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

/**
 * Signals the unauthenticated state. ProtectedRoute / route guards catch
 * this and navigate to the React /login page, preserving the original
 * destination via location state.
 *
 * IMPORTANT: do NOT redirect to Espo `/` here — that bounces to the
 * Backbone shell and breaks the React-owned auth flow.
 */
export class UnauthenticatedError extends Error {
  constructor() {
    super("unauthenticated");
    this.name = "UnauthenticatedError";
  }
}

/**
 * EspoCRM кладе UTF-8 повідомлення помилки СИРИМИ байтами в HTTP-заголовок
 * X-Status-Reason; fetch читає його як latin1 → mojibake (напр. duplicate-email
 * UA-текст). Відновлюємо: latin1-рядок → байти → decode UTF-8. ASCII/коректні
 * рядки повертаються незмінними (fatal-roundtrip + fallback). D-044 S1-hotfix.
 */
function decodeHeaderUtf8(s: string): string {
  try {
    const bytes = Uint8Array.from(s, (c) => c.charCodeAt(0) & 0xff);
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return s;
  }
}

async function parseError(r: Response): Promise<ApiClientError> {
  let code = `http_${r.status}`;
  let message = r.statusText || "Request failed";
  let details: Record<string, unknown> | undefined;
  let traceId: string | undefined;

  const statusReason = r.headers.get("X-Status-Reason");
  if (statusReason) {
    message = decodeHeaderUtf8(statusReason);
  }

  try {
    const text = await r.text();
    if (text) {
      const body = JSON.parse(text) as ApiErrorBody;
      if (body?.error) {
        code = body.error.code ?? code;
        message = body.error.messageUa ?? body.error.message ?? message;
        details = body.error.details;
        traceId = body.error.traceId;
      } else {
        // D-055 S2: 422-тунель validation-помилок (InteractionReportService /
        // ReportCPlusPolicy) шле ПЛАСКЕ тіло {code, message} без .error-обгортки.
        const flat = body as unknown as { code?: string; message?: string };
        if (flat?.code) {
          code = flat.code;
          if (flat.message) message = flat.message;
          // S3: тунель несе доп. поля (suggestionKey тощо) — у details.
          details = body as unknown as Record<string, unknown>;
        }
      }
    }
  } catch {
    // non-JSON body — keep header-derived message
  }

  return new ApiClientError(r.status, code, message, details, traceId);
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
  /** D-044 S5 — fetch keepalive: survives tab close (beforeunload best-effort exit). */
  keepalive?: boolean;
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, query, signal, keepalive } = opts;
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v != null) url.searchParams.set(k, String(v));
    }
  }

  const headers: Record<string, string> = {
    Accept: "application/json",
    "X-Requested-With": "XMLHttpRequest",
  };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  // Espo auth: userName + token from successful login, echoed on every request.
  // Format mirrors stock Backbone client: base64(userName + ':' + token).
  const creds = getAuthCredentials();
  if (creds) {
    const enc = btoa(unescape(encodeURIComponent(`${creds.userName}:${creds.token}`)));
    headers["Espo-Authorization"] = enc;
    headers["Authorization"] = `Basic ${enc}`;
    headers["Espo-Authorization-By-Token"] = "true";
  }

  if (method !== "GET") {
    const xsrf = readCookie("XSRF-TOKEN");
    if (xsrf) headers["X-XSRF-Token"] = xsrf;
  }

  // D-044 S5 — активний view-as: bearer-пара сесії на КОЖЕН запит (server-side
  // swap + read-only 403). Контрольні view-as шляхи — від реального адміна.
  const viewAs = viewAsHeaderValue();
  if (viewAs && !isViewAsControlPath(path)) {
    headers["X-Kormax-View-As"] = viewAs;
  }

  const r = await fetch(url.toString(), {
    method,
    headers,
    credentials: "include",
    signal,
    keepalive,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  // Diagnostic: surface every /me result so we can correlate with login attempts.
  if (path === "/Custom/frontend/me") {
    console.warn("[KORMAX-AUTH]", "[apiFetch]", "/me", {
      status: r.status,
      sent_auth: !!creds,
      userName: creds?.userName,
      token_tail: creds?.token ? `…${creds.token.slice(-6)}` : null,
    });
  }

  if (r.status === 401) throw new UnauthenticatedError();
  if (!r.ok) throw await parseError(r);
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

/**
 * D-143 PDF-превʼю: той самий транспорт/auth, що apiFetch, але тіло — Blob
 * (binary). Помилки — той самий parseError (flat-тунель {code,message,details}
 * читається як у JSON-запитах → annexErrorCode бачить pdf_preview_unavailable).
 */
export async function apiFetchBlob(path: string, opts: RequestOptions = {}): Promise<Blob> {
  const { method = "GET", body, query, signal } = opts;
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v != null) url.searchParams.set(k, String(v));
    }
  }

  const headers: Record<string, string> = {
    Accept: "application/pdf, application/json",
    "X-Requested-With": "XMLHttpRequest",
  };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  const creds = getAuthCredentials();
  if (creds) {
    const enc = btoa(unescape(encodeURIComponent(`${creds.userName}:${creds.token}`)));
    headers["Espo-Authorization"] = enc;
    headers["Authorization"] = `Basic ${enc}`;
    headers["Espo-Authorization-By-Token"] = "true";
  }

  if (method !== "GET") {
    const xsrf = readCookie("XSRF-TOKEN");
    if (xsrf) headers["X-XSRF-Token"] = xsrf;
  }

  const viewAs = viewAsHeaderValue();
  if (viewAs && !isViewAsControlPath(path)) {
    headers["X-Kormax-View-As"] = viewAs;
  }

  const r = await fetch(url.toString(), {
    method,
    headers,
    credentials: "include",
    signal,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (r.status === 401) throw new UnauthenticatedError();
  if (!r.ok) throw await parseError(r);
  return r.blob();
}
